import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
    Alert,
    Link,
    Button
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDocumentTitle } from "../../Hooks/useDocumentTitle";
import {
    DashboardStatisticsDto,
    EUserGroup,
    EVersionStage,
    RecentVersionDto,
    SupportContractSummaryDto,
    YouTrackStatisticsDto,
    YouTrackIssueDTO
} from "../../apiClient/Api";
import { useApplicationContext } from "../../ApplicationContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getStageLabel } from "../../utils/versionUtils";
import {
    faFileContract,
    faTicket,
    faBan,
    faCodeBranch,
    faChartLine,
    faExclamationTriangle,
    faCheckCircle,
    faClock,
    faComments,
    faLifeRing,
    faKey,
    faUsers,
    faQrcode
} from '@fortawesome/pro-solid-svg-icons';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';

dayjs.extend(relativeTime);

export const AdminDashboard = () => {
    useDocumentTitle("base.pages.dashboard");
    
    const [dashboardStats, setDashboardStats] = useState<DashboardStatisticsDto | null>(null);
    const [contractSummary, setContractSummary] = useState<SupportContractSummaryDto | null>(null);
    const [ticketStats, setTicketStats] = useState<YouTrackStatisticsDto | null>(null);
    const [openTickets, setOpenTickets] = useState<YouTrackIssueDTO[]>([]);
    const [loadingDashboard, setLoadingDashboard] = useState(true);
    const [loadingContracts, setLoadingContracts] = useState(true);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [loadingOpenTickets, setLoadingOpenTickets] = useState(true);
    const [errorDashboard, setErrorDashboard] = useState<string | null>(null);
    const [errorContracts, setErrorContracts] = useState<string | null>(null);
    const [errorTickets, setErrorTickets] = useState<string | null>(null);
    const [errorOpenTickets, setErrorOpenTickets] = useState<string | null>(null);
    
    const context = useApplicationContext();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();


    // Set dayjs locale based on i18n
    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);

    /**
     * Fetches dashboard statistics
     */
    const fetchDashboardStats = async () => {
        try {
            setLoadingDashboard(true);
            setErrorDashboard(null);
            const response = await context.api.dashboard.dashboardGetStatistics({ days: 30, limit: 5, expiringSoonDays: 30 });
            setDashboardStats(response.data);
        } catch (err: any) {
            console.error("Error fetching dashboard stats:", err);
            setErrorDashboard(t('dashboard.errorLoadingData'));
            context.apiError(err);
        } finally {
            setLoadingDashboard(false);
        }
    };

    /**
     * Fetches contract summary
     */
    const fetchContractSummary = async () => {
        try {
            setLoadingContracts(true);
            setErrorContracts(null);
            const response = await context.api.supportContract.supportContractGetSummary({ expiringSoonDays: 30 });
            setContractSummary(response.data);
        } catch (err: any) {
            console.error("Error fetching contract summary:", err);
            setErrorContracts(t('dashboard.errorLoadingData'));
            context.apiError(err);
        } finally {
            setLoadingContracts(false);
        }
    };

    /**
     * Fetches ticket statistics
     */
    const fetchTicketStats = async () => {
        try {
            console.log("Fetching ticket stats...");
            setLoadingTickets(true);
            setErrorTickets(null);
            const response = await context.api.youTrack.youTrackGetStatistics({ top: 500 });
            console.log("Ticket stats response:", response.data);
            setTicketStats(response.data);
        } catch (err: any) {
            console.error("Error fetching ticket stats:", err);
            setErrorTickets(t('dashboard.errorLoadingData'));
            context.apiError(err);
        } finally {
            setLoadingTickets(false);
        }
    };

    /**
     * Fetches open tickets list
     */
    const fetchOpenTickets = async () => {
        try {
            setLoadingOpenTickets(true);
            setErrorOpenTickets(null);
            const response = await context.api.youTrack.youTrackGetTickets({ top: 100, onlyOpen: true });
            setOpenTickets(response.data);
        } catch (err: any) {
            console.error("Error fetching open tickets:", err);
            setErrorOpenTickets(t('dashboard.errorLoadingData'));
            context.apiError(err);
        } finally {
            setLoadingOpenTickets(false);
        }
    };

    useEffect(() => {
        // Fetch all data in parallel
        fetchDashboardStats();
        // Contract summary is only for real admins (not OFFICE users)
        if (context.userData?.userGroup !== undefined && context.userData.userGroup < EUserGroup.OFFICE) {
            fetchContractSummary();
        }
        fetchTicketStats();
        fetchOpenTickets();
    }, []);

    /**
     * Formats version number
     */
    const formatVersion = (version: RecentVersionDto): string => {
        return `${version.major}.${version.minor}.${version.patch}`;
    };


    /**
     * Renders a statistic card
     */
    const StatCard = ({ 
        title, 
        value, 
        icon, 
        color, 
        subtitle, 
        onClick 
    }: { 
        title: string; 
        value: number | string; 
        icon: any; 
        color: string; 
        subtitle?: string;
        onClick?: () => void;
    }) => (
        <Card 
            sx={{ 
                height: '100%',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.3s',
                '&:hover': onClick ? {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                } : {}
            }}
            onClick={onClick}
        >
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                        <Typography color="text.secondary" variant="caption" sx={{ display: 'block' }}>
                            {title}
                        </Typography>
                        <Typography variant="h3" component="div" sx={{ mt: 1, fontWeight: 'bold' }}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: color,
                            borderRadius: 2,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <FontAwesomeIcon icon={icon} fontSize={24} color="#fff" />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    const isAdmin = context.userData?.userGroup !== undefined && context.userData.userGroup <= EUserGroup.OFFICE;

    // Prepare contract chart data
    const contractChartData = contractSummary ? [
        { id: 0, value: contractSummary.activeContracts || 0, label: t('dashboard.activeContracts'), color: '#4caf50' },
        { id: 1, value: contractSummary.expiringSoonContracts || 0, label: t('dashboard.expiringSoon'), color: '#ff9800' },
        { id: 2, value: contractSummary.expiredContracts || 0, label: t('dashboard.expired'), color: '#f44336' },
        { id: 3, value: contractSummary.infiniteContracts || 0, label: t('dashboard.unlimited'), color: '#2196f3' },
    ] : [];

    // Prepare ticket chart data - filter out "Solved" status only if there are other statuses
    const allTicketStatuses = ticketStats?.ticketsByStatus || [];
    const nonSolvedStatuses = allTicketStatuses.filter(item => item.status?.toLowerCase() !== 'solved');
    const ticketChartData = (nonSolvedStatuses.length > 0 ? nonSolvedStatuses : allTicketStatuses)
        .map((item, index) => ({
            status: item.status || 'Unknown',
            count: item.count || 0
        }));

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                {t('dashboard.welcome', { name: context.userData?.name || '' })}
            </Typography>

            {/* Main Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    {loadingDashboard ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                                    <CircularProgress size={40} />
                                </Box>
                            </CardContent>
                        </Card>
                    ) : errorDashboard ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Alert severity="error" sx={{ height: '100%' }}>{errorDashboard}</Alert>
                            </CardContent>
                        </Card>
                    ) : (
                        <StatCard
                            title={t('dashboard.activeContracts')}
                            value={dashboardStats?.activeContracts || 0}
                            icon={faFileContract}
                            color="#4caf50"
                            onClick={() => navigate(isAdmin ? '/admin/support-contracts' : '/support-contracts')}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {loadingDashboard ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                                    <CircularProgress size={40} />
                                </Box>
                            </CardContent>
                        </Card>
                    ) : errorDashboard ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Alert severity="error" sx={{ height: '100%' }}>{errorDashboard}</Alert>
                            </CardContent>
                        </Card>
                    ) : (
                        <StatCard
                            title={t('dashboard.expiringSoon')}
                            value={dashboardStats?.expiringSoonContracts || 0}
                            icon={faExclamationTriangle}
                            color="#ff9800"
                            subtitle={t('dashboard.next30Days')}
                            onClick={() => navigate(isAdmin ? '/admin/support-contracts' : '/support-contracts')}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {loadingDashboard ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                                    <CircularProgress size={40} />
                                </Box>
                            </CardContent>
                        </Card>
                    ) : errorDashboard ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Alert severity="error" sx={{ height: '100%' }}>{errorDashboard}</Alert>
                            </CardContent>
                        </Card>
                    ) : (
                        <StatCard
                            title={t('dashboard.expiredContracts')}
                            value={dashboardStats?.expiredContracts || 0}
                            icon={faBan}
                            color="#f44336"
                            onClick={() => navigate(isAdmin ? '/admin/support-contracts' : '/support-contracts')}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {loadingDashboard || loadingTickets ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                                    <CircularProgress size={40} />
                                </Box>
                            </CardContent>
                        </Card>
                    ) : errorDashboard || errorTickets ? (
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Alert severity="error" sx={{ height: '100%' }}>{errorDashboard || errorTickets}</Alert>
                            </CardContent>
                        </Card>
                    ) : (
                        <StatCard
                            title={t('dashboard.openTickets')}
                            value={
                                context.userData?.userGroup !== undefined && context.userData.userGroup <= EUserGroup.DEVELOPMENT
                                    ? (ticketStats?.totalOpenHelpdeskTickets || 0)
                                    : (dashboardStats?.openTickets || 0)
                            }
                            icon={faTicket}
                            color="#2196f3"
                            onClick={() => navigate('/tickets')}
                        />
                    )}
                </Grid>
            </Grid>

            {/* Quick Actions */}
            <Card sx={{ mb: 3 }}>
                <CardHeader 
                    title={t('dashboard.quickActions')}
                    avatar={<FontAwesomeIcon icon={faComments} />}
                />
                <CardContent>
                    <Grid container spacing={2}>
                        {isAdmin ? (
                            <>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faKey} />}
                                        onClick={() => navigate('/admin/password_generator')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.generatePassword')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faCodeBranch} />}
                                        onClick={() => navigate('/versions/zbe')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.viewVersions')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faQrcode} />}
                                        onClick={() => navigate(isAdmin ? '/admin/support-contracts' : '/support-contracts')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.manageContracts')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faUsers} />}
                                        onClick={() => navigate('/admin/users')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.manageUsers')}
                                    </Button>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faLifeRing} />}
                                        onClick={() => navigate('/support')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.createSupportRequest')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faCodeBranch} />}
                                        onClick={() => navigate('/versions/zbe')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.viewVersions')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faQrcode} />}
                                        onClick={() => navigate('/support-contracts')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.viewContracts')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faTicket} />}
                                        onClick={() => navigate('/tickets')}
                                        sx={{ py: 1.5 }}
                                    >
                                        {t('dashboard.viewTickets')}
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Contract Summary Chart */}
                {isAdmin && (
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardHeader 
                                title={t('dashboard.contractDistribution')}
                                avatar={<FontAwesomeIcon icon={faFileContract} />}
                            />
                            <CardContent>
                                {loadingContracts ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : errorContracts ? (
                                    <Alert severity="error">{errorContracts}</Alert>
                                ) : contractSummary ? (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300 }}>
                                            <PieChart
                                                series={[
                                                    {
                                                        data: contractChartData,
                                                        highlightScope: { fade: 'global', highlight: 'item' },
                                                        faded: { innerRadius: 30, additionalRadius: -30 },
                                                    },
                                                ]}
                                                width={400}
                                                height={300}
                                            />
                                        </Box>
                                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                            <Typography variant="body2">
                                                <strong>{t('dashboard.total')}:</strong> {contractSummary.totalContracts}
                                            </Typography>
                                        </Stack>
                                    </>
                                ) : null}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Ticket Statistics Chart */}
                <Grid item xs={12} md={isAdmin ? 6 : 12}>
                    <Card>
                        <CardHeader 
                            title={t('dashboard.ticketsByStatus')}
                            avatar={<FontAwesomeIcon icon={faChartLine} />}
                            subheader={
                                isAdmin && !loadingTickets && ticketStats?.averageResponseTimeHours !== null && ticketStats?.averageResponseTimeHours !== undefined ? (
                                    <Typography variant="body2" color="text.secondary">
                                        <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} />
                                        {t('dashboard.avgResponseTime')}: {ticketStats.averageResponseTimeHours.toFixed(1)}h
                                        {ticketStats.responseTimeSampleSize && ` (${t('dashboard.basedOn')} ${ticketStats.responseTimeSampleSize} ${t('dashboard.tickets')})`}
                                    </Typography>
                                ) : null
                            }
                        />
                        <CardContent>
                            {loadingTickets ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                                    <CircularProgress />
                                </Box>
                            ) : errorTickets ? (
                                <Alert severity="error">{errorTickets}</Alert>
                            ) : ticketStats && ticketChartData.length > 0 ? (
                                <>
                                    <BarChart
                                        xAxis={[{ 
                                            scaleType: 'band', 
                                            data: ticketChartData.map(item => item.status),
                                            tickLabelStyle: { fontSize: 12 }
                                        }]}
                                        series={[{ 
                                            data: ticketChartData.map(item => item.count),
                                            color: '#2196f3'
                                        }]}
                                        width={isAdmin ? 500 : 1000}
                                        height={300}
                                    />
                                    <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 2 }}>
                                        <Chip 
                                            icon={<FontAwesomeIcon icon={faTicket} />}
                                            label={`${t('dashboard.open')}: ${ticketStats.openTickets}`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip 
                                            icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                            label={`${t('dashboard.closed')}: ${ticketStats.closedTickets}`}
                                            color="success"
                                            variant="outlined"
                                        />
                                    </Stack>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('dashboard.noData')}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Versions */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader 
                            title={t('dashboard.recentVersions')}
                            avatar={<FontAwesomeIcon icon={faCodeBranch} />}
                            subheader={!loadingDashboard && dashboardStats ? t('dashboard.last30Days', { count: dashboardStats.recentVersionsCount || 0 }) : undefined}
                        />
                        <CardContent>
                            {loadingDashboard ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                    <CircularProgress />
                                </Box>
                            ) : errorDashboard ? (
                                <Alert severity="error">{errorDashboard}</Alert>
                            ) : dashboardStats?.recentVersions && dashboardStats.recentVersions.length > 0 ? (
                                <List>
                                    {dashboardStats.recentVersions.map((version, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                border: 1,
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                mb: 1,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                            onClick={() => navigate(`/versions/V${version.major}.${version.minor}.${version.patch}_${version.productId}`)}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="h6" component="span">
                                                            v{formatVersion(version)}
                                                        </Typography>
                                                        { version.stage !== EVersionStage.RELEASE &&  <Chip 
                                                                label={getStageLabel(version.stage,t)} 
                                                                size="small" 
                                                                color={version.stage === EVersionStage.ALPHA ? 'error' : 'warning'}
                                                            />}

                                                        
                                                        {version.criticalUpdate && (
                                                            <Chip 
                                                                label={t('dashboard.critical')} 
                                                                size="small" 
                                                                color="error"
                                                            />
                                                        )}
                                                    </Stack>
                                                }
                                                secondary={
                                                    <Stack spacing={0.5}>
                                                        {version.subtitle && (
                                                            <Typography variant="body2">
                                                                {version.subtitle}
                                                            </Typography>
                                                        )}
                                                        {version.productName && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {version.productName}
                                                            </Typography>
                                                        )}
                                                        {version.approved && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {dayjs(version.approved).fromNow()}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('dashboard.noRecentVersions')}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Open Tickets List */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader 
                            title={t('dashboard.recentOpenTickets')}
                            avatar={<FontAwesomeIcon icon={faComments} />}
                            subheader={!loadingOpenTickets && openTickets && openTickets.length > 0 
                                ? t('dashboard.showingTickets', { 
                                    shown: Math.min(5, openTickets.length),
                                    total: openTickets.length 
                                })
                                : undefined
                            }
                            action={
                                !loadingOpenTickets && openTickets && openTickets.length > 0 ? (
                                    <IconButton onClick={() => navigate('/tickets')}>
                                        <Typography variant="caption">{t('dashboard.viewAll')}</Typography>
                                    </IconButton>
                                ) : null
                            }
                        />
                        <CardContent>
                            {loadingOpenTickets ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                    <CircularProgress />
                                </Box>
                            ) : errorOpenTickets ? (
                                <Alert severity="error">{errorOpenTickets}</Alert>
                            ) : openTickets && openTickets.length > 0 ? (
                                <List>
                                    {openTickets.slice(0, 5).map((ticket, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                border: 1,
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                mb: 1,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                            onClick={() => navigate(`/tickets/${ticket.idReadable}`)}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {ticket.idReadable}
                                                        </Typography>
                                                        {ticket.status && (
                                                            <Chip 
                                                                label={ticket.status} 
                                                                size="small" 
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Stack>
                                                }
                                                secondary={
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="body2">
                                                            {ticket.summary}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1}>
                                                            {ticket.supportId && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    ID: {ticket.supportId}
                                                                </Typography>
                                                            )}
                                                            {ticket.created && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {dayjs(ticket.created).fromNow()}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#4caf50" style={{ marginBottom: 16 }} />
                                    <Typography variant="h6" color="text.secondary">
                                        {t('dashboard.noOpenTickets')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {t('dashboard.allTicketsClosed')}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};
